import storage from '~/utils/storage';

const tokenValidityWindow = 600 * 1000; // 10 minutes
const refreshWindow = 2 * 24 * 60 * 60 * 1000; // 2 days

export function getToken(): string | null {
    const token = storage.getItem(TOKEN);
    if (isExpired()) {
        if (isImpersonating()) {
            restoreAdminToken();
            return getToken();
        } else {
            clearToken();
            return null;
        }
    }

    return token;
}

export function getRefreshToken(remove = false): string | null {
    const token = storage.getItem(REFRESH);
    // We need to remove the token to avoid multiple calls to refresh. If the
    // token stays, the useEffect in which the refresh is made will make the call
    // 2 times.
    if (remove) {
        storage.removeItem(REFRESH);
    }
    return token;
}

function isExpired(): boolean {
    const expiration = new Date(Number(storage.getItem(EXPIRATION) || 0) * 1000);
    return (
        isNaN(expiration.getTime()) ||
        expiration.getTime() - new Date().getTime() < tokenValidityWindow
    );
}

export function shouldRefresh(): boolean {
    if (isImpersonating()) return false;
    const expiration = new Date(Number(storage.getItem(EXPIRATION) || 0) * 1000);
    if (isNaN(expiration.getTime())) {
        return false;
    }

    if (!getRefreshToken()) {
        return false;
    }

    return expiration.getTime() - new Date().getTime() < refreshWindow;
}

export const TOKEN = 'rwt_t';
export const EXPIRATION = 'rwt_e';
export const REFRESH = 'rwt_r';
export const ADMIN_TOKEN = 'rwat_t';
export const ADMIN_EXPIRATION = 'rwat_e';
export const ADMIN_REFRESH = 'rwat_r';

export function storeImpersonatedToken(
    token: string | null,
    expiration: string | number | null,
): void {
    if (isImpersonating()) {
        restoreAdminToken();
    }
    storeAdminToken(storage.getItem(TOKEN), storage.getItem(EXPIRATION), storage.getItem(REFRESH));
    storeToken(token, expiration);
}

export function restoreAdminToken(): void {
    storeToken(
        storage.getItem(ADMIN_TOKEN),
        storage.getItem(ADMIN_EXPIRATION),
        storage.getItem(ADMIN_REFRESH),
    );
    clearAdminToken();
}

export function isImpersonating(): boolean {
    const token = storage.getItem(ADMIN_TOKEN);
    const expiration = new Date(Number(storage.getItem(ADMIN_EXPIRATION) || 0) * 1000);
    return (
        Boolean(token) &&
        !isNaN(expiration.getTime()) &&
        expiration.getTime() > new Date().getTime()
    );
}

export function storeAdminToken(
    token: string | null,
    expiration: string | number | null,
    refresh?: string | null,
): void {
    storage.setItem(ADMIN_TOKEN, token);
    storage.setItem(ADMIN_EXPIRATION, expiration);
    storage.setItem(ADMIN_REFRESH, refresh);
}

export function storeToken(
    token: string | null,
    expiration: string | number | null,
    refresh: string | null = '',
): void {
    storage.setItem(TOKEN, token);
    storage.setItem(EXPIRATION, expiration);
    storage.setItem(REFRESH, refresh);
}

export function clearToken(): void {
    storage.removeItem(TOKEN);
    storage.removeItem(EXPIRATION);
    storage.removeItem(REFRESH);
}

export function clearAdminToken(): void {
    storage.removeItem(ADMIN_TOKEN);
    storage.removeItem(ADMIN_EXPIRATION);
    storage.removeItem(ADMIN_REFRESH);
}
